import { useCallback, useState } from "react";
import { useVoiceControl, VoiceButton } from "../voice/VoiceProvider";
import { heardAny } from "../voice/match";
import { useContent, useT } from "../i18n/LocaleProvider";

interface Props {
  onHome: () => void;
}


// Public Web3Forms access key. Safe to ship in client code — it only allows
// sending messages to the site owner, never reading them.
const WEB3FORMS_KEY = "61421e56-9f27-4b6d-ba9c-bd7878100628";

// Submitted rating values stay English in every language, so the site owner's
// inbox reads consistently. Only the visible labels are translated.
const RATING_VALUES = [
  "⭐⭐⭐⭐⭐ Loved it",
  "⭐⭐⭐⭐ Really good",
  "⭐⭐⭐ It's okay",
  "⭐⭐ Needs work",
  "⭐ Not for us",
] as const;

type Status = "idle" | "sending" | "success" | "error";

export function ContactPage({ onHome }: Props) {
  const t = useT();
  const { contactPoints } = useContent();
  const [status, setStatus] = useState<Status>("idle");

  const onCommand = useCallback(
    (transcript: string): boolean => {
      if (heardAny(transcript, [...t.commands.home, ...t.commands.back])) {
        onHome();
        return true;
      }
      return false;
    },
    [onHome, t],
  );
  useVoiceControl(onCommand);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      });
      const data = await res.json();
      if (data.success) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }, []);

  return (
    <div className="facts-page">
      <header className="player-header">
        <button className="home-btn" onClick={onHome} aria-label={t.backToHome}>
          {t.home}
        </button>
        <h1>{t.contactTitle}</h1>
      </header>

      <p className="subtitle">{t.contactSubtitle}</p>

      <div className="control-bar">
        <VoiceButton />
      </div>

      <div className="facts-grid">
        {contactPoints.map((n, i) => (
          <div className="fact-card" key={i}>
            <div className="fact-emoji" aria-hidden="true">
              {n.emoji}
            </div>
            <p className="fact-text">{n.text}</p>
          </div>
        ))}
      </div>

      {/* Feedback form (for grown-ups). Submissions are emailed to us via
          Web3Forms — no backend, no cookies. */}
      <section className="feedback" aria-label={t.feedbackAria}>
        <h2 className="feedback-title">{t.feedbackTitle}</h2>
        <p className="feedback-intro">{t.feedbackIntro}</p>

        {status === "success" ? (
          <div className="feedback-thanks" role="status">
            <span className="feedback-thanks-emoji" aria-hidden="true">
              🎉
            </span>
            <p>{t.feedbackThanks}</p>
            <button className="facts-btn" onClick={() => setStatus("idle")}>
              {t.feedbackSendAnother}
            </button>
          </div>
        ) : (
          <form className="feedback-form" onSubmit={handleSubmit}>
            <input type="hidden" name="access_key" value={WEB3FORMS_KEY} />
            <input type="hidden" name="subject" value="New Learn 2 Draw feedback 🎨" />
            <input type="hidden" name="from_name" value="Learn 2 Draw" />
            {/* Honeypot — hidden from people, catches bots. */}
            <input
              type="checkbox"
              name="botcheck"
              className="feedback-hp"
              tabIndex={-1}
              autoComplete="off"
            />

            <label className="feedback-field">
              <span>{t.feedbackHowFun}</span>
              <select name="rating" defaultValue="">
                <option value="">{t.feedbackRatingPick}</option>
                {RATING_VALUES.map((value, i) => (
                  <option key={value} value={value}>
                    {t.feedbackRatings[i]}
                  </option>
                ))}
              </select>
            </label>

            <label className="feedback-field">
              <span>{t.feedbackMessage}</span>
              <textarea
                name="message"
                rows={4}
                required
                placeholder={t.feedbackMessagePlaceholder}
              />
            </label>

            <label className="feedback-field">
              <span>{t.feedbackName}</span>
              <input
                type="text"
                name="name"
                autoComplete="name"
                placeholder={t.feedbackNamePlaceholder}
              />
            </label>

            <label className="feedback-field">
              <span>{t.feedbackEmail}</span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@example.com"
              />
            </label>

            {status === "error" && (
              <p className="feedback-err" role="alert">
                {t.feedbackErrMsg}
              </p>
            )}

            <button
              type="submit"
              className="feedback-submit"
              disabled={status === "sending"}
            >
              {status === "sending" ? t.feedbackSending : t.feedbackSend}
            </button>
          </form>
        )}
      </section>

      <p className="made-with-love">{t.madeWithLove}</p>
    </div>
  );
}
